import { useParams } from 'react-router-dom'
import { profilesCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Button } from '@/ui/Button/Button'
import { Chip } from '@/ui/Chip/Chip'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { useMeQuery } from '../hooks/queries'
import { useNow } from '../hooks/useNow'
import { useWorkspaceMembersQuery } from '../hooks/useWorkspaceMembers'
import { formatRelative } from '../utils/time'
import { UserModelSection } from './workspace/UserModelSection'

/**
 * Full-width Person Profile page showing identity header, role, joined date,
 * and user model / observations.
 */
export function PersonProfilePage() {
  const { workspaceId = '', username: rawUsername = '' } = useParams<{
    workspaceId: string
    username: string
  }>()
  const username = decodeURIComponent(rawUsername)
  const now = useNow()
  const { data: me } = useMeQuery()
  const ws = me?.workspaces.find((w) => w.workspace_id === workspaceId)
  const isSelf = me?.username === username

  const { data, isPending, isError, refetch } = useWorkspaceMembersQuery(
    workspaceId,
    {
      enabled: !!workspaceId,
      staleTime: 60_000,
    },
  )
  const members = data ?? []
  const member = members.find((m) => m.username === username)
  const showMetaSkeleton = useSkeletonDelay(isPending)

  return (
    <WsPageShell
      crumbs={[
        {
          label: ws?.name ?? profilesCopy.workspaceCrumb,
          to: ROUTES.home,
        },
        { label: member?.username ?? username },
      ]}
      lead={
        <Avatar name={member?.username ?? username} kind="human" size="lg" />
      }
      title={member?.username ?? username}
      meta={
        isPending ? (
          <span aria-busy="true">{showMetaSkeleton && <Skeleton />}</span>
        ) : !data ? undefined : member ? (
          profilesCopy.person.metaJoined(
            member.role ?? profilesCopy.person.defaultRole,
            formatRelative(member.created_at, now),
          )
        ) : (
          profilesCopy.person.meta(profilesCopy.person.defaultRole)
        )
      }
      actions={
        isSelf ? (
          <Chip variant="kind" label={profilesCopy.person.youChip} />
        ) : undefined
      }
    >
      <Stack gap="space-24">
        {isError && (
          <Alert tone={data ? 'warning' : 'danger'}>
            <Inline gap="space-8" align="baseline">
              <Text>
                {data
                  ? profilesCopy.refreshMembersFailed
                  : profilesCopy.loadMembersFailed}
              </Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                {profilesCopy.retry}
              </Button>
            </Inline>
          </Alert>
        )}
        <Stack as="section" gap="space-12">
          <SectionHeader rule>
            {profilesCopy.person.userModelTitle}
          </SectionHeader>
          <Text as="p" tone="text-mute">
            {profilesCopy.person.userModelIntro}
          </Text>
          <UserModelSection
            wsId={workspaceId}
            username={isSelf ? null : username}
          />
        </Stack>
      </Stack>
    </WsPageShell>
  )
}
